import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, Button, CardActions } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { useNavigate, useLocation } from 'react-router-dom';

const useTreeItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      color: theme.palette.text.secondary,
      margin: 0,
      paddingLeft: 0,
      listStyle: 'none',
      '&:focus > $content, &$selected > $content': {
        backgroundColor: `var(--tree-view-bg-color)`,
        color: 'var(--tree-view-color)',
      },
      '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label': {
        backgroundColor: 'transparent',
      },
      '&:hover > $content $label, &:hover > $content $label, &$selected > $content $label': {
        backgroundColor: 'transparent',
      },
      '&.MuiCardContent-root': {
        padding: 0,
      },
    },
    cardCategory: {
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
      border: '2px solid, #F5F6F9',
      borderRadius: 24
    },
    cardButtons: {
      justifyContent: 'center',
      // padding: 16,
    },
    buttons: {
      marginRight: theme.spacing(2),
    },
    button: {
      '&:active': {
        opacity: 0.2,
      },
    },
    content: {
      color: theme.palette.text.primary,
      fontWeight: 500,
      '$expanded > &': {
        fontWeight: theme.typography.fontWeightRegular,
      },
    },
    group: {
      marginLeft: 0,
    },
    expanded: {},
    selected: {
      backgroundColor: `var(--tree-view-bg-color)`,
    },
    label: {
      fontWeight: 'inherit',
      color: 'inherit',
    },
    labelRoot: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px',
      width: '100%',
      cursor: 'pointer',
      position: 'relative',
      transition: '.25s ease-out',
    },
    labelContainerChildren: {
      maxHeight: '100%',
      overflow: 'hidden',
    },
    labelChildren: {
      display: 'flex',
      alignItems: 'center',
      paddingBottom: theme.spacing(0.7),
      paddingTop: theme.spacing(0.7),
      width: '100%',
      padding: 10,
      cursor: 'pointer',
      paddingLeft: 30,
      transition: '.25s ease-out',
    },
    labelIcon: {},
    labelTitle: {
      fontWeight: 500,
      flexGrow: 1,
      fontSize: 18,
      color: '#000',
      textTransform: 'uppercase',
    },
    labelText: {
      fontWeight: 500,
      flexGrow: 1,
      fontSize: 16,
      color: '#21366C',
      textTransform: 'none',
    },
    activeItem: {
      backgroundColor: '#F5F6F9',
      borderRadius: 12
    },
    hide: {
      height: 0,
    },
  })
);

const navigateList = [
  { title: 'Users', link: '/users/buyers', id: 1 },
  { title: 'Balance requsts', link: '/balancerequests', id: 2 },
  { title: 'Send/Receive Request', link: '/sendReceiveRequest', id: 3 },
  { title: 'Transactions', link: '/transactions', id: 4 },
  
];

type TestProps = {
  select: (id: number) => void;
  navigateList: typeof navigateList;
  selectChildrenId: number | null;
  setSelectChildrenId: (id: number) => void;
  locale: string;
  setSelectedCategoryTypes?: (value: any[]) => void;
  selectedCategoryId?: number | null;
};

export const StyledTreeItem = ({
  navigateList, 
  select,
  setSelectChildrenId,
  selectChildrenId,
  locale,
  setSelectedCategoryTypes,
  selectedCategoryId,
}: TestProps) => {
  return (
    <li>
      {navigateList.map(item => (
        <StyledTreeSubItem
          key={item.id}
          category={item} 
          select={select}
          selectChildrenId={selectChildrenId}
          setSelectChildrenId={setSelectChildrenId}
          locale={locale}
          setSelectedCategoryTypes={setSelectedCategoryTypes}
          selectedCategoryId={selectedCategoryId}
        />
      ))}
    </li>
  );
};

export const StyledTreeSubItem = ({
  category,
  select,
  setSelectChildrenId,
  selectChildrenId,
  setSelectedCategoryTypes,
  selectedCategoryId,
}: any) => {
  const classes = useTreeItemStyles();
  const [selectChild, setSelectChild] = useState<boolean>(false);
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const handleItem = (id: number) => {
    select(id);
    category && category.id && setSelectChildrenId(category.id);
  };

  const onClickHandler = (link: string) => {
    localStorage.setItem(`${category.id}selectChild`, JSON.stringify(!selectChild));
    setSelectChild(!selectChild);
    navigate(link)
  };

  useEffect(() => {
    selectedCategoryId !== selectChildrenId &&
      selectedCategoryId === category?.id &&
      setSelectChildrenId(selectedCategoryId!!);
  }, [selectedCategoryId]);

  return (
    <>
      <li style={{ marginLeft: '10px' }}>
        <div
          onClick={() => {
            onClickHandler(category.link);
            category && setSelectedCategoryTypes && setSelectedCategoryTypes([]);
            category && category.id && handleItem(category.id);
            category && category.id && setSelectChildrenId(category.id);
          }}
          className={clsx(classes.labelRoot, {
            [classes.activeItem]: location.pathname === category.link,
          })}
        >
          <Typography variant="body2" className={classes.labelText}>
            {category.title}
          </Typography>
        </div>
      </li>
    </>
  );
};

const FilterCategoriesTree: React.FC<any> = ({
  setSelectedCategoryId,
  selectedCategoryId,
}) => {
  const intl = useIntl();
  const classes = useTreeItemStyles();
  const [selectChildrenId, setSelectChildrenId] = useState<number | null>(0);

  const select = (id: number) => {
    setSelectedCategoryId(id);
  };

  return (
    <Card className={classes.cardCategory}>
      <CardContent>
        <ul className={classes.root}>
          <StyledTreeItem
            navigateList={navigateList} 
            select={select}
            setSelectChildrenId={setSelectChildrenId}
            selectChildrenId={selectChildrenId}
            locale={intl.locale}
            selectedCategoryId={selectedCategoryId}
          />
        </ul>
      </CardContent>
    </Card>
  );
};

export default FilterCategoriesTree;
